'use client';

import Link from 'next/link';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { ButtonIcon } from '@eliadmin/core/src/components/ui/button-icon';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';

import { SectionHeading } from '@eliadmin/core/src/components/ui/section-heading';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { MdArrowCircleRight } from 'react-icons/md';
import { ContactForm } from '@eliadmin/core/src/components/contact/contact-form';

export function Contact({
  generalLayout,
  title,
  description,
}: {
  generalLayout: GeneralLayout['data'];
  title: string;
  description: string;
}) {
  const { t } = useTranslations();

  const cards = [
    {
      icon: '/images/icons/contact/call.svg',
      label: t('text800'),
      value: generalLayout?.contact?.phone || '',
      href: `tel:${generalLayout?.contact?.phone || ''}`,
    },
    {
      icon: '/images/icons/contact/email.svg',
      label: t('text569'),
      value: generalLayout?.contact?.email || '',
      href: `mailto:${generalLayout?.contact?.email || ''}`,
    },
  ];

  return (
    <section className={cn('custom-section', 'pb-10 pt-16 sm:pb-[50px] sm:pt-20 lg:pt-[100px]')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 sm:gap-8 md:flex-row md:justify-between lg:gap-12',
        )}
      >
        <div className="flex w-full max-w-[486px] flex-col items-start justify-start gap-6 sm:gap-8 lg:gap-[50px]">
          <SectionHeading align="left" title={title} innerHtml={description} />
          <div className="flex w-full flex-col items-start justify-start gap-3">
            {cards.map(card => (
              <Link
                key={card.label}
                href={card?.href}
                target="_blank"
                prefetch={false}
                className="ea-rounded-xl flex w-full items-center justify-between gap-4 border p-6 transition-all hover:bg-border/25"
              >
                <div className="flex max-h-12 min-h-12 min-w-12 max-w-12 items-center justify-center rounded-[12px] bg-[#E4F2E7]">
                  <ButtonIcon
                    alt={card.label}
                    url={card.icon}
                    className="max-h-6 min-h-6 min-w-6 max-w-6"
                  />
                </div>
                <div className="flex w-full flex-1 flex-col items-start justify-start">
                  <Paragraph color="neutral" className="w-full font-semibold" size="sm">
                    {card.label}
                  </Paragraph>
                  <Paragraph color="primary" className="w-full font-semibold" size="base">
                    {card.value}
                  </Paragraph>
                </div>
                <MdArrowCircleRight className="max-h-5 min-h-5 min-w-5 max-w-5 text-primary" />
              </Link>
            ))}
          </div>
        </div>
        <div className="w-full md:max-w-[634px]">
          <ContactForm />
        </div>
      </div>
    </section>
  );
}
